import { AfterViewInit, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { PortalEventTemplate } from '@src/app/model/portal-event-template';
import { Observable, Subscription } from 'rxjs';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { PortalInvariantTemplate } from '@src/app/model/portal-invariant-template';
import { Events, Id, id } from '@ess/jg-rule-executor';
import { PortalInvariant } from '@src/app/model/portal-invariant';
import { RuleEditorModalComponent } from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.component';
import { cloneDeep } from 'lodash';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  createRuleListFromEvents,
  createRuleListFromInvariants,
  RuleData,
} from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.utils';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { fixedEnvironment } from '@src/environments/environment.fixed';

type RuleTemplate = (PortalEventTemplate | PortalInvariantTemplate);

@Component({
  selector: 'app-event-template-list',
  templateUrl: './rule-template-list.component.html',
  styleUrls: ['./rule-template-list.component.scss'],
  standalone: false,
})
export class RuleTemplateListComponent implements OnInit, AfterViewInit, OnDestroy {
  public displayedColumns: string[] = ['id', 'name', 'action'];
  public currentRuleTemplates: MatTableDataSource<RuleTemplate> = new MatTableDataSource<RuleTemplate>([]);
  public allModi = ['event', 'module', 'environment'];
  public mode: 'event' | 'module' | 'environment' = 'event';

  private _subs: Subscription = new Subscription();
  private _fetchFailMsg: string = 'ruleTemplate.upload.failure';
  private _eventFailMsg: string = 'blueprints.upload.failure';

  readonly sort = viewChild(MatSort);

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _translateService: TranslateService
  ) {
  }

  public ngOnInit(): void {
    this._setRuleTemplates();
  }

  public ngAfterViewInit(): void {
    this.currentRuleTemplates.sort = this.sort();
  }

  public openTemplateDialog(
    emptyTemplate: RuleTemplate,
    newFn: (json: JSON) => Observable<boolean>,
    updateFn: (id: Id<PortalEventTemplate | PortalInvariantTemplate>, json: JSON) => Observable<boolean>,
    refreshFn: () => void,
    name: string,
    copy: boolean,
    ruleTemplate?: RuleTemplate
  ): void {
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      name,
      ruleTemplate ?? emptyTemplate,
      'ruleTemplate.upload.header',
      'buttons.submit',
      ['invariants', 'events']
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      json => (ruleTemplate && !copy) ? updateFn(ruleTemplate.id, json) : newFn(json),
      this._translateService.instant(this._fetchFailMsg),
      refreshFn
    );
  }

  public openRuleTemplateDialog(ruleTemplate?: RuleTemplate, copy: boolean = false): void {
    if (this.mode === 'environment') {
      this.openTemplateDialog(
        new PortalInvariantTemplate(id(''), [], ''),
        this._apolloService.newEnvInvariantTemplate$.bind(this._apolloService),
        this._apolloService.updateEnvInvariantTemplate$.bind(this._apolloService),
        this._apolloService.getAllEnvInvariantTemplatesRefresh.bind(this._apolloService),
        'invariantTemplate',
        copy,
        ruleTemplate
      );
    } else if (this.mode === 'module') {
      this.openTemplateDialog(
        new PortalInvariantTemplate(id(''), [], ''),
        this._apolloService.newModuleInvariantTemplate$.bind(this._apolloService),
        this._apolloService.updateModuleInvariantTemplate$.bind(this._apolloService),
        this._apolloService.getAllModuleInvariantTemplatesRefresh.bind(this._apolloService),
        'invariantTemplate',
        copy,
        ruleTemplate
      );
    } else if (this.mode === 'event') {
      this.openTemplateDialog(
        new PortalEventTemplate(id(''), new Events(), ''),
        this._apolloService.newEventTemplate$.bind(this._apolloService),
        this._apolloService.updateEventTemplate$.bind(this._apolloService),
        this._apolloService.getAllEventTemplatesRefresh.bind(this._apolloService),
        'eventTemplate',
        copy,
        ruleTemplate
      );
    }
  }

  public openEditRuleDialog(ruleTemplate?: RuleTemplate, type: string = 'module'): void {
    const additionalFields = new PortalInvariant(
      id('templateId'), // Will be replaced
      '',
      new Map(),
      new Map(),
      '',
      ''
    );

    // Set up the rule list
    let ruleList: RuleData[] = [];
    if (ruleTemplate instanceof PortalInvariantTemplate) {
      ruleList = createRuleListFromInvariants(ruleTemplate.invariants);
    } else if (ruleTemplate instanceof PortalEventTemplate) {
      ruleList = createRuleListFromEvents(ruleTemplate.events);
    }

    // Open the dialog
    const dialogRef = this._dialog.open(RuleEditorModalComponent, {
      data: {
        ruleTypeText: type,
        ruleList,
        crud: true,
        readOnly: false,
        additionalFieldsTemplate: additionalFields,
        baseTranslationKey: 'ruleTemplate.updateEvents',
        showAdditional: type !== 'event',
      },
      minWidth: fixedEnvironment.jsonModalWidthLarge,
    });

    const updater = (updated) => {
      const updateFn: (
        id: Id<PortalEventTemplate | PortalInvariantTemplate>,
        json: JSON,
        showAlert: boolean
      ) => Observable<boolean> = type === 'environment'
        ? this._apolloService.updateEnvInvariantTemplate$.bind(this._apolloService)
        : (type === 'module'
          ? this._apolloService.updateModuleInvariantTemplate$.bind(this._apolloService)
          : this._apolloService.updateEventTemplate$.bind(this._apolloService));

      // Parse the edited rules
      const updatedRt = cloneDeep(ruleTemplate);
      if (ruleTemplate instanceof PortalEventTemplate) {
        const updatedEvents = updated.map(x => x.ruleCode);
        (updatedRt as PortalEventTemplate).events = new Events(
          updatedEvents[0] && updatedEvents[0] !== '' ? updatedEvents[0] : undefined,
          updatedEvents[1] && updatedEvents[1] !== '' ? updatedEvents[1] : undefined,
          updatedEvents[2] && updatedEvents[2] !== '' ? updatedEvents[2] : undefined,
          updatedEvents[3] && updatedEvents[3] !== '' ? updatedEvents[3] : undefined
        );
      } else if (ruleTemplate instanceof PortalInvariantTemplate) {
        (updatedRt as PortalInvariantTemplate).invariants = updated.map((x) => {
          const updatedInvariant = x.additionalFields as PortalInvariant;
          updatedInvariant.id = id(x.ruleId);
          updatedInvariant.rule = x.ruleCode;
          updatedInvariant.correction = x.correctionCode;
          return updatedInvariant;
        });
      }

      const newRtInput: JSON = (type === 'event'
        ? { eventTemplate: updatedRt.toGqlInput() }
        : { invariantTemplate: updatedRt.toGqlInput() }) as object as JSON;

      return updateFn(updatedRt.id, newRtInput, false);
    };

    const refreshFn: () => void
      = type === 'environment'
        ? this._apolloService.getAllEnvInvariantTemplatesRefresh.bind(this._apolloService)
        : (type === 'module'
          ? this._apolloService.getAllModuleInvariantTemplatesRefresh.bind(this._apolloService)
          : this._apolloService.getAllEventTemplatesRefresh.bind(this._apolloService));

    JsonModal.useRuleDialog(
      dialogRef,
      updater,
      this._translateService.instant(this._eventFailMsg, { ruleType: type }),
      refreshFn,
      dialogRef.componentInstance.showError.bind(dialogRef.componentInstance)
    );
  }

  public updateRuleMode($event): void {
    this._setRuleTemplates();
    this.mode = $event.value;
  }

  private _setRuleTemplates() {
    if (this.mode === 'event') {
      this._subs.add(this._apolloService.getAllEventTemplates$().subscribe((ts) => {
        this.currentRuleTemplates.data = ts.eventTemplates;
      }));
    } else if (this.mode === 'module') {
      this._subs.add(this._apolloService.getAllModuleInvariantTemplates$().subscribe((ts) => {
        this.currentRuleTemplates.data = ts.invariantTemplates;
      }));
    } else if (this.mode === 'environment') {
      this._subs.add(this._apolloService.getAllEnvInvariantTemplates$().subscribe((ts) => {
        this.currentRuleTemplates.data = ts.invariantTemplates;
      }));
    }
  }

  public sortChange(sortState: Sort): void {
  }

  public ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
